import {Alert, Button, Space, Table, message} from "antd";
import type {ColumnsType} from "antd/es/table";
import {CloseOutlined, DownloadOutlined} from "@ant-design/icons";
import {useState} from "react";
import {StyledDescriptionText} from "../../../../messages/messages.style";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {
  StyledNoUpcomingCallBox,
  StyledUpcomingCallsWrapper,
} from "../../../upcoming-calls/upcoming-calls.style";
import {SessionDetailsDrawerBookingDetails} from "./booking-details";
import {StyledSessionDetailsDrawer} from "./style";
import {
  useSessionCustomers,
  useExportSessionCustomersCSV,
  SessionCustomer,
} from "../../../../../features/sessions";
import {FullPageLoader} from "../../../../full-page-loader";

// V2 data type (one row per customer, with their latest paid booking)
interface V2DataType {
  key: string;
  bookingReference: string;
  name: string;
  email: string;
  lastBookingDate: string;
  lastBookingTime: string;
  amountPaid: string;
}

type TSessionCustomerInsightsProps = {
  sessionId: number;
  startTime: string;
  duration: string | number;
  date: string;
};

const PER_PAGE = 5;

/** Formats a `YYYY-MM-DD` booking date as a local calendar date, or "-" when absent. */
function formatBookingDate(bookingDate: string) {
  const [year, month, day] = bookingDate.split("-").map(Number);
  if (!year || !month || !day) {
    return "-";
  }
  return new Date(year, month - 1, day).toLocaleDateString();
}

export function SessionCustomerInsights({sessionId}: TSessionCustomerInsightsProps) {
  const [page, setPage] = useState(1);
  const [isSessionDetailsDrawerOpen, setIsSessionDetailsDrawerOpen] = useState(false);

  const v2CustomersQuery = useSessionCustomers(sessionId, {page, perPage: PER_PAGE});

  // V2 CSV export
  const exportCsvMutation = useExportSessionCustomersCSV();

  // V2 drawer state
  const [v2CustomerDetails, setV2CustomerDetails] = useState<V2DataType>();

  // Loading state
  const isLoading = v2CustomersQuery.isLoading;
  const isFetching = v2CustomersQuery.isFetching;
  const isError = v2CustomersQuery.isError;
  const error = v2CustomersQuery.error;

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (isError) {
    return (
      <Alert
        message="Error"
        description={error?.message || "Failed to load customers"}
        type="error"
        showIcon
      />
    );
  }

  function closeSessionDetailsDrawer() {
    setIsSessionDetailsDrawerOpen(false);
    setV2CustomerDetails(undefined);
  }

  const handleExportAsCSV = () => {
    exportCsvMutation.mutate(sessionId, {
      onSuccess: (blob) => {
        message.success("File ready to be downloaded!");
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `session-${sessionId}-customers.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const v2Data = v2CustomersQuery.data;
  const customers: V2DataType[] =
    v2Data?.data.map((customer: SessionCustomer) => ({
      key: customer.bookingReference,
      bookingReference: customer.bookingReference,
      name: customer.name,
      email: customer.email ? customer.email.toLowerCase() : "-",
      lastBookingDate: formatBookingDate(customer.lastBookingDate),
      lastBookingTime: customer.lastBookingTime || "-",
      amountPaid:
        customer.amountPaid === 0
          ? "Free"
          : `${customer.currency} ${customer.amountPaid.toLocaleString()}`,
    })) || [];

  const showV2CustomerDetails = (customer: V2DataType) => {
    setV2CustomerDetails(customer);
    setIsSessionDetailsDrawerOpen(true);
  };

  const v2Columns: ColumnsType<V2DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "14rem",
      key: "name",
      render: (text) => (
        <StyledDescriptionText style={{textTransform: "capitalize"}}>
          {text}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "16rem",
      key: "email",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "Last Booking",
      dataIndex: "lastBookingDate",
      width: "10rem",
      key: "lastBookingDate",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "Time",
      dataIndex: "lastBookingTime",
      width: "12rem",
      key: "lastBookingTime",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amountPaid",
      width: "10rem",
      key: "amountPaid",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "",
      dataIndex: "action",
      width: "10rem",
      key: "action",
      render: (_, record) => (
        <Button
          onClick={() => showV2CustomerDetails(record)}
          block
          type="text"
          size="small"
        >
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  const hasNextPage = v2Data?.hasNextPage ?? false;
  const total = v2Data?.total ?? 0;

  return (
    <>
      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          <StyledPageHeader>Customers</StyledPageHeader>
          <div>
            <Button
              onClick={handleExportAsCSV}
              disabled={customers.length < 1}
              block
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              loading={exportCsvMutation.isPending}
              size="small"
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </div>
        </div>
        {customers.length < 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your customers will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <>
            <Table
              columns={v2Columns}
              dataSource={customers}
              loading={isFetching}
              scroll={{x: 100}}
              pagination={false}
            />
            <div style={{marginTop: "2rem", display: "flex", gap: "1rem"}}>
              <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span style={{padding: "0.5rem 1rem"}}>Page {page}</span>
              <Button disabled={!hasNextPage} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </>
        )}
      </div>
      {v2CustomerDetails && (
        <StyledSessionDetailsDrawer
          title="Customer details"
          placement="right"
          onClose={closeSessionDetailsDrawer}
          visible={isSessionDetailsDrawerOpen}
          closable={false}
          extra={
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={closeSessionDetailsDrawer}
            ></Button>
          }
        >
          <div>
            <Space direction="vertical" style={{display: "flex"}} size={24}>
              <SessionDetailsDrawerBookingDetails
                data={[
                  {title: "Customer's name", value: v2CustomerDetails.name},
                  {title: "Customer's email", value: v2CustomerDetails.email},
                  {title: "Booking reference", value: v2CustomerDetails.bookingReference},
                  {title: "Last booking", value: v2CustomerDetails.lastBookingDate},
                  {title: "Time", value: v2CustomerDetails.lastBookingTime},
                  {title: "Amount paid", value: v2CustomerDetails.amountPaid},
                ]}
              />
            </Space>
          </div>
        </StyledSessionDetailsDrawer>
      )}
    </>
  );
}
